import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { ReactNode } from "react"
import { useEffect, useRef } from "react"
import { EntityListSkeleton } from "../shared/EntityListSkeleton"

interface Props<T> {
  rows: T[]
  isLoading: boolean
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  onFetchMore?: () => void
  renderRow: (row: T) => ReactNode
  selectedKey?: string
  /** Total row count from the paginated API response; when set, replaces the plain Load More footer with a "Showing X of Y" + Load More row. */
  totalCount?: number
}

export function MasterDataList<T>({
  rows,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onFetchMore,
  renderRow,
  selectedKey,
  totalCount,
}: Props<T>) {
  const { translate } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!selectedKey || !containerRef.current) return
    // Safe today since ids are UUIDs, but CSS.escape() keeps this from
    // breaking if that ever stops being true (same fix as MappingColumnList).
    const el = containerRef.current.querySelector(
      `[data-row-id="${CSS.escape(selectedKey)}"]`,
    )
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [selectedKey])

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto">
      {isLoading ? <EntityListSkeleton /> : rows.map((row) => renderRow(row))}
      {totalCount !== undefined
        ? !isLoading && (
            <div className="flex items-center justify-between gap-2 border-t border-grey4 px-4 py-3 text-xs text-grey5">
              <span>
                {translate("MC_MD_LIST_SHOWING_PRE")} {rows.length}{" "}
                {translate("MC_MD_LIST_SHOWING_OF")} {totalCount}{" "}
                {translate("MC_MD_TOTAL_RECORDS")}
              </span>
              {hasNextPage && (
                <button
                  type="button"
                  onClick={onFetchMore}
                  disabled={isFetchingNextPage}
                  className="shrink-0 font-medium text-grey5 hover:text-white disabled:opacity-50"
                >
                  {isFetchingNextPage
                    ? translate("MC_SUBTABLE_LOADING")
                    : translate("MC_MD_LIST_LOAD_MORE")}
                </button>
              )}
            </div>
          )
        : hasNextPage && (
            <button
              type="button"
              onClick={onFetchMore}
              disabled={isFetchingNextPage}
              className="w-full border-t border-grey4 py-3 text-xs text-grey5 hover:text-white disabled:opacity-50"
            >
              {isFetchingNextPage
                ? translate("MC_SUBTABLE_LOADING")
                : translate("MC_MD_LIST_LOAD_MORE")}
            </button>
          )}
    </div>
  )
}
